import { useQueries, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react'
import type { Place, WeatherReport } from '../../shared/contract'
import { fillReport, fillText, hasTokens } from '../../shared/text'
import { US_UNITS, type Units } from '../../shared/units'
import { FIVE_MIN, weatherQueryOptions } from '../api'
import { isEarlyReport, isSettledReport, useBootGate, warmPaintPending } from '../data/boot'
import { selectedPlace, useApp } from '../store'

/**
 * Presentation gate (UI v3, rule R3). The data layer streams several reports for a cold build:
 * the persisted one, the edge snapshot, the worker's first partial, the complete report. Painting
 * each in turn is what made values and icons flip between sources in the first seconds.
 *
 * The presentation policy, in order of what a person sees:
 *  1. WARM (a persisted report, or a settled edge/complete one): painted at once, final. When it is fresh
 *     (< `FRESH_MS` old) nothing visible changes afterwards until the next refresh; when older, one update.
 *  2. COLD (only a lite/partial report so far): the value slots stay skeletons (no numbers) for up to
 *     `HOLD_MS` while a settled report is awaited; the page then paints ONCE, from whichever is best.
 *  3. After that paint, at most ONE consolidated update: the first settled report replaces what
 *     MATERIALLY changed (`calm`) in a single commit, so every changed value cross-fades together.
 *     Gaps stay with their displayed generation; no value appears or changes again for `QUIET_MS` (a normal
 *     refresh interval), unless the person asks (`releaseUpdates`: the refresh button).
 * The live query now publishes only its settled promise; the early-report budget below remains for legacy consumers.
 */

/** Fields that say where and when a report came from: a change there rewrites no number on the page. */
const PROVENANCE = new Set(['generated_at', 'sources', 'attribution', 'fusion'])
const visiblyChanged = (a: WeatherReport, b: WeatherReport) =>
  (Object.keys(b) as (keyof WeatherReport)[]).some((k) => !PROVENANCE.has(k) && a[k] !== b[k])

/** Cold load: how long a place may stay a skeleton waiting for a settled report. */
export const HOLD_MS = 1000
/** After a visible update, shown values stay put this long (the refresh interval, less its jitter). */
export const QUIET_MS = FIVE_MIN - 20_000
/** A settled report younger than this is final when painted (the edge snapshot's own cache life): the browser's later report waits. */
export const FRESH_MS = 10 * 60_000

type LatLon = Pick<Place, 'latitude' | 'longitude'>

const keyOf = (p: LatLon) => `${p.latitude.toFixed(3)},${p.longitude.toFixed(3)}`

const samePlace = (a: WeatherReport, b: WeatherReport) =>
  Math.abs(a.location.latitude - b.location.latitude) <= 0.011 && Math.abs(a.location.longitude - b.location.longitude) <= 0.011

/** True when `next` has a section `shown` lacks and the page can use it without moving anything. */
export function fillMissing(shown: WeatherReport, incoming: WeatherReport): WeatherReport {
  const patch: Partial<WeatherReport> = {}
  if (!shown.hourly.length && incoming.hourly.length) {
    patch.hourly = incoming.hourly
    patch.daily = incoming.daily
    patch.summaries = incoming.summaries
    patch.fusion = incoming.fusion
  }
  if (shown.minutely.status === 'unavailable' && incoming.minutely.status !== 'unavailable') patch.minutely = incoming.minutely
  if (!shown.air_quality && incoming.air_quality) patch.air_quality = incoming.air_quality
  if (!shown.tides && incoming.tides) patch.tides = incoming.tides
  if (!shown.averages && incoming.averages) patch.averages = incoming.averages
  if (!shown.alerts.length && incoming.alerts.length) patch.alerts = incoming.alerts
  if (shown.current.uv_index == null && incoming.current.uv_index != null) patch.current = { ...shown.current, uv_index: incoming.current.uv_index }
  const keys = Object.keys(patch)
  if (!keys.length) return shown
  // Source rows follow the sections they describe, so "still loading" labels clear with them.
  return { ...shown, ...patch, sources: incoming.sources, attribution: [...new Set([...shown.attribution, ...incoming.attribution])] }
}

/**
 * How far a number may move and still read as the same value, by field name. Forecast hours and
 * days (`loose`) get more room than the current conditions: two degrees in hour 14 is noise, two
 * degrees in the number the hero shows is not.
 */
function tolerance(key: string, loose: boolean): number {
  if (key === 'confidence') return 0.08
  if (/_pct$|probability/.test(key)) return loose ? 10 : 6
  if (/_c$|^low$|^high$/.test(key)) return loose ? 1.1 : 0.8 // about 2 °F / 1.5 °F
  if (/_hpa$/.test(key)) return 1.5
  if (/_ms$/.test(key)) return loose ? 1.5 : 1 // about 3 / 2 mph
  if (/_deg$/.test(key)) return 12
  if (/_m$/.test(key)) return 800
  if (/_(mm|cm|mmh)$/.test(key)) return 0.25
  if (/^aqi$/.test(key)) return 3
  if (/uv/.test(key)) return 0.6
  return 0
}

/** The observation's own timestamp is a label, not a value: a newer reading of the same weather is the same weather. */
const LABEL_KEYS = new Set(['time'])

/** True when the two values show the same thing to a person (no material difference). */
export function immaterial(a: unknown, b: unknown, key = '', loose = false): boolean {
  if (a === b) return true
  // A sentence quotes its numbers in unit tokens: two sentences that read the same in whole degrees and mph are the same.
  if (typeof a === 'string' && typeof b === 'string' && hasTokens(a) && hasTokens(b)) return fillText(a, US_UNITS) === fillText(b, US_UNITS)
  if (typeof a === 'number' && typeof b === 'number') return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= tolerance(key, loose)
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => immaterial(x, b[i], key, loose))
  if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
    const ao = a as Record<string, unknown>
    const bo = b as Record<string, unknown>
    const keys = Object.keys(ao)
    if (keys.length !== Object.keys(bo).length) return false
    return keys.every((k) => k in bo && (LABEL_KEYS.has(k) || immaterial(ao[k], bo[k], k, loose)))
  }
  return false
}

/**
 * One hour of the forecast, leaf by leaf: a number that did not materially move keeps its shown
 * value even when something else in the hour (its condition, its rain type) did change. Hours carry no
 * sentence that quotes their numbers, so this can never leave text beside the wrong figure.
 */
function mergeHour(prev: unknown, next: unknown, key: string): unknown {
  if (prev === next) return prev
  if (typeof prev === 'number' && typeof next === 'number') return immaterial(prev, next, key, true) ? prev : next
  if (prev && next && typeof prev === 'object' && typeof next === 'object' && !Array.isArray(prev) && !Array.isArray(next)) {
    const po = prev as Record<string, unknown>
    const no = next as Record<string, unknown>
    const out: Record<string, unknown> = {}
    let same = Object.keys(po).length === Object.keys(no).length
    for (const k of Object.keys(no)) {
      out[k] = k in po ? mergeHour(po[k], no[k], k) : no[k]
      if (out[k] !== po[k]) same = false
    }
    return same ? prev : out
  }
  return next
}

/**
 * The complete report arriving over the one on screen: keep every section, hour and day whose
 * values did not materially change (same object, so nothing re-renders and nothing flickers),
 * take the new one where they did. Days are judged one by one and a section as a whole, so a sentence
 * is never shown beside numbers from a different report; hours are merged number by number.
 */
export function calm(shown: WeatherReport, incoming: WeatherReport): WeatherReport {
  const out = { ...shown } as Record<string, unknown>
  const from = shown as unknown as Record<string, unknown>
  let changed = false
  for (const [key, next] of Object.entries(incoming as unknown as Record<string, unknown>)) {
    const prev = from[key]
    if (prev === next) continue
    let section: unknown = next
    if (Array.isArray(prev) && Array.isArray(next) && next.length > 0) {
      // Hours and days are matched by their own time/date (the complete report is usually longer than
      // the first), anything else by position.
      const stamp = (el: unknown) => (el && typeof el === 'object' ? ((el as Record<string, unknown>).time ?? (el as Record<string, unknown>).date) : undefined)
      const keyed = typeof stamp(next[0]) === 'string' && typeof stamp(prev[0]) === 'string'
      const byStamp = keyed ? new Map(prev.map((el) => [stamp(el), el])) : null
      const merged = next.map((el, i) => {
        const before = byStamp ? byStamp.get(stamp(el)) : prev[i]
        if (before === undefined) return el
        return immaterial(before, el, key, true) ? before : key === 'hourly' ? mergeHour(before, el, key) : el
      })
      section = merged.length === prev.length && merged.every((el, i) => el === prev[i]) ? prev : merged
    } else if (immaterial(prev, next, key)) {
      section = prev
    }
    if (section !== prev) {
      out[key] = section
      changed = true
    }
  }
  return changed ? (out as unknown as WeatherReport) : shown
}

/**
 * The next report to show. `early` is injected for tests; the app reads the data layer's marks.
 * - nothing shown yet → the first report, whatever it is;
 * - a different place (My Location moved) → replaced;
 * - an early report → may only fill empty sections;
 * - anything else (the complete report, a refresh, a rebuild) → what materially changed replaces,
 *   and its values cross-fade; the rest stays exactly as it was (`calm`).
 */
export function nextShown(shown: WeatherReport | undefined, incoming: WeatherReport, early: (r: WeatherReport) => boolean = isEarlyReport): WeatherReport {
  if (!shown || shown === incoming) return incoming
  if (!samePlace(shown, incoming)) return incoming
  if (early(incoming)) return fillMissing(shown, incoming)
  return calm(shown, incoming)
}

interface Entry {
  incoming: WeatherReport
  shown: WeatherReport | undefined
  /** When a held or deferred answer must be recomputed, though the input has not changed. */
  recheckAt?: number
  /** Earliest time the next visible change of a shown value may be applied. */
  nextChangeAt: number
}
const entries = new Map<string, Entry>()
/** When a place was first asked for: the cold-load budget runs from there. */
const wantedAt = new Map<string, number>()
const timers = new Map<string, ReturnType<typeof setTimeout>>()

// A held or deferred answer needs a re-run when its time comes, though no new report arrives.
let version = 0
const listeners = new Set<() => void>()
const subscribe = (cb: () => void) => {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
const getVersion = () => version
function bump() {
  version++
  for (const l of listeners) l()
}
function recheckAt(key: string, at: number, now: number) {
  clearTimeout(timers.get(key))
  timers.set(key, setTimeout(() => {
    timers.delete(key)
    bump()
  }, Math.max(0, at - now) + 4))
}

/** A place is on its way to the screen: its cold-load budget starts now (idempotent). */
export function wantPlace(key: string, now = Date.now()): void {
  if (!wantedAt.has(key)) wantedAt.set(key, now)
}

/**
 * The person asked for fresher numbers (the refresh button): the next settled report may change what
 * is shown at once, without waiting out the quiet period.
 */
export function releaseUpdates(): void {
  for (const e of entries.values()) e.nextChangeAt = 0
  bump()
}

/**
 * What to present for `key` given the newest report (`undefined` = keep the skeleton). Idempotent per
 * input: every observer of a place sees the same sequence and the same answer, until a held or
 * deferred answer comes due.
 */
export function gate(key: string, incoming: WeatherReport, now = Date.now()): WeatherReport | undefined {
  const entry = entries.get(key)
  if (entry && entry.incoming === incoming && (entry.recheckAt === undefined || now < entry.recheckAt)) return entry.shown
  const shown = entry?.shown
  let next: WeatherReport | undefined
  let again: number | undefined
  let nextChangeAt = entry?.nextChangeAt ?? 0
  if (!shown) {
    const deadline = (wantedAt.get(key) ?? now) + HOLD_MS
    if (isSettledReport(incoming) || now >= deadline) {
      next = incoming
      // A fresh settled report (persisted a moment ago, or the edge's full snapshot) is the paint AND the update:
      // what the browser builds a few seconds later waits for the next refresh. An old one is updated once.
      if (isSettledReport(incoming) && now - Date.parse(incoming.generated_at) < FRESH_MS) nextChangeAt = now + QUIET_MS
    } else again = deadline
  } else if (shown === incoming) {
    next = shown
  } else if (!samePlace(shown, incoming)) {
    next = incoming
  } else {
    // Keep one complete generation, including its gaps, through the quiet period. Alerts have an independent reader.
    const filled = now >= nextChangeAt ? fillMissing(shown, incoming) : shown
    next = filled
    if (isSettledReport(incoming)) {
      const merged = calm(filled, incoming)
      if (now >= nextChangeAt) {
        if (visiblyChanged(filled, merged)) nextChangeAt = now + QUIET_MS
        next = merged
      } else if (visiblyChanged(filled, merged)) again = nextChangeAt
      else next = shown
    }
  }
  entries.set(key, { incoming, shown: next, recheckAt: again, nextChangeAt })
  if (again !== undefined) recheckAt(key, again, now)
  return next
}

const rendered = new WeakMap<WeatherReport, { units: Units; report: WeatherReport }>()

/**
 * The report with its generated sentences ("High 72°…", "Gusts to 30 mph") rendered in the reader's
 * units. Reports hold those sentences unit-neutral, so changing units re-renders the text from what
 * is already here: nothing is refetched and no number has to be recomputed.
 */
export function localizeReport(report: WeatherReport, units: Units): WeatherReport {
  const hit = rendered.get(report)
  if (hit && hit.units === units) return hit.report
  const out = fillReport(report, units)
  rendered.set(report, { units, report: out })
  return out
}

/** Tests: forget what was shown. */
export function resetDisplayGate(): void {
  entries.clear()
  wantedAt.clear()
  for (const t of timers.values()) clearTimeout(t)
  timers.clear()
  version++
}

/** At start-up only the selected place fetches until its first report lands (data/boot.ts). */
function useFetchAllowed(): (p: LatLon) => boolean {
  const open = useBootGate()
  const saveData = typeof navigator !== 'undefined' && (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData
  const selected = useApp((s) => open && !saveData ? '' : keyOf(selectedPlace(s)))
  return useCallback((p: LatLon) => (open && !saveData) || (!warmPaintPending() && keyOf(p) === selected), [open, saveData, selected])
}

/**
 * The report to PRESENT for a place (undefined until the first trustworthy report exists).
 * `select` narrows it to the sections a module draws, so a module re-renders only when its own
 * inputs change.
 */
export function useDisplayReport<T = WeatherReport>(
  place: LatLon & { isCurrent?: boolean },
  select?: (report: WeatherReport) => T,
) {
  const allowed = useFetchAllowed()
  const key = keyOf(place)
  if (allowed(place)) wantPlace(key) // the budget starts when the place starts loading, not when it is merely listed
  const due = useSyncExternalStore(subscribe, getVersion, () => 0)
  const units = useApp((s) => s.units)
  const selectRef = useRef(select)
  selectRef.current = select
  const pick = useCallback(
    (data: WeatherReport) => {
      const gated = gate(key, data)
      const shown = gated && localizeReport(gated, units)
      return (shown && selectRef.current ? selectRef.current(shown) : shown) as T
    },
    // `due` re-runs it when a held or deferred answer comes due; `units` re-renders its sentences.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, due, units],
  )
  // The gate/localizer already owns report identity. Query's per-observer deep copy breaks the prepared-place caches.
  const q = useQuery({ ...weatherQueryOptions(place), enabled: allowed(place), select: pick, structuralSharing: false })
  // The previous place's report stays up for My Location only (it moves; a skeleton flash is worse).
  const hasData = q.data !== undefined && (!q.isPlaceholderData || !!place.isCurrent)
  return { ...q, data: hasData ? (q.data as T) : undefined }
}

/**
 * The same gated reports for several places at once (the places list). One entry per place, in
 * order; `data` is undefined until that place's first trustworthy report exists. It never mixes
 * with `useDisplayReport`'s answer for the same place: both go through `gate`.
 */
export function useDisplayReports(places: (LatLon & { isCurrent?: boolean })[]) {
  const allowed = useFetchAllowed()
  const units = useApp((s) => s.units)
  const due = useSyncExternalStore(subscribe, getVersion, () => 0)
  const queries = useMemo(() => places.map((p) => {
      const key = keyOf(p)
      if (allowed(p)) wantPlace(key)
      return { ...weatherQueryOptions(p), placeholderData: undefined, enabled: allowed(p), structuralSharing: false, select: (data: WeatherReport) => {
        const shown = gate(key, data)
        return shown && localizeReport(shown, units)
      } }
    }), [places, allowed, units, due])
  return useQueries({ queries })
}
